import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useForm, Controller } from 'react-hook-form';
import { Label, TextInput, FieldError } from '~/App/Components/Forms';
import { ErrorMessage } from '@hookform/error-message';
import Select from '~/App/Components/Select';
import { countriesApi } from '../api';
import { useQuery } from 'react-query';
import { CardElement } from '@stripe/react-stripe-js';
import { BillingDetails, CreditCardValue } from '~/types/stripe';
import colours from '~/utils/colours';
import { TextInputType } from '~/App/Components/Forms/TextInput';
import { isEmpty } from 'lodash';

export enum CreditCardFormDefaults {
  ID = 'credit_card_form',
}

export interface CreditCardInputError {
  setup_intent_id: string;
  message: string;
}

export interface CreditCardFormProps {
  id?: string;
  className?: string;
  onSubmit(
    data: BillingDetails,
    reset: (values?: Record<string, unknown>) => void
  ): Promise<void>;
  creditCardInputError?: CreditCardInputError;
}

const FormGroup = styled.div`
  position: relative;
  padding-bottom: 17px;
  margin-bottom: 5px;
  width: 100%;
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
`;

const LeftColumn = styled.div`
  width: 100%;
  max-width: 400px;
`;

const RightColumn = styled.div`
  padding-left: 15px;
  flex: 1;
  margin-left: auto;
`;

const StateZip = styled.div`
  display: flex;
  align-items: center;
`;

const StateGroup = styled(FormGroup)`
  width: 48%;
  position: relative;
`;

const ZipGroup = styled(StateGroup)`
  margin-left: auto;
`;

const StyledFieldError = styled(FieldError)`
  position: absolute;
  bottom: 0;
`;

const CARD_INPUT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '16px',
      color: colours.greyDarkAlt2,
      '::placeholder': {
        color: colours.greyLightAlt3,
      },
      lineHeight: '40px',
    },
  },
};

export interface CreditCardFormData {
  address: string;
  city: string;
  country: {
    value: string;
    label: string;
  };
  credit_card: CreditCardValue;
  name: string;
  state: string;
  zip: string;
}

const CardInputWrapper = styled.div<{ invalid: boolean | null }>`
  padding: 0 20px;
  border-radius: 5px;
  background: ${colours.contentMainBg};
  border: 1px solid
    ${(props) => (props.invalid ? colours.red : colours.borderLightGrey)};
`;

const CreditCardForm = ({
  className,
  onSubmit,
  creditCardInputError,
  id = CreditCardFormDefaults.ID,
}: CreditCardFormProps) => {
  const [isCreditCardInvalid, setIsCreditCardInvalid] =
    useState<boolean>(false);

  const { data: countries } = useQuery('load-countries', () =>
    countriesApi().getAll()
  );

  const { control, register, handleSubmit, errors, reset, setError } =
    useForm<CreditCardFormData>();

  useEffect(() => {
    setError('credit_card', {
      type: 'manual',
      message: creditCardInputError?.message,
    });
  }, [creditCardInputError]);

  const validateCreditCard = (value?: CreditCardValue) => {
    if (isEmpty(value)) {
      setIsCreditCardInvalid(true);
      return 'Field is required';
    }

    if (value?.error) {
      setIsCreditCardInvalid(true);
      return value.error.message;
    }

    setIsCreditCardInvalid(false);
    return true;
  };

  const handleFormSubmit = (formData: CreditCardFormData) => {
    const billingDetails: BillingDetails = {
      name: formData.name,
      address: {
        city: formData.city,
        country: formData.country.value,
        postal_code: formData.zip,
        state: formData.state,
        line1: formData.address,
      },
    };

    onSubmit(billingDetails, reset);
  };

  return (
    <div className={className}>
      <form
        id={id}
        onSubmit={handleSubmit(handleFormSubmit) as unknown as () => void}
      >
        <FormRow>
          <FormGroup>
            <Label text='Card details' htmlFor='card_details' />
            <CardInputWrapper invalid={isCreditCardInvalid}>
              <Controller
                control={control}
                name='credit_card'
                defaultValue={null}
                rules={{
                  validate: validateCreditCard,
                }}
                render={({ onChange }) => (
                  <CardElement
                    onChange={onChange}
                    options={CARD_INPUT_OPTIONS}
                  />
                )}
              />
            </CardInputWrapper>
            <ErrorMessage
              errors={errors}
              name='credit_card'
              as={StyledFieldError}
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <LeftColumn>
            <FormGroup>
              <Label text='Cardholder name' htmlFor='card_name' />
              <TextInput
                type={TextInputType.Text}
                id='card_name'
                placeholder='John Doe'
                name='name'
                ref={register({
                  required: 'Field is required',
                })}
                error={errors.name}
              />
              <ErrorMessage errors={errors} name='name' as={StyledFieldError} />
            </FormGroup>
          </LeftColumn>
        </FormRow>

        <FormRow>
          <LeftColumn>
            <FormGroup>
              <Label text='Address' htmlFor='card_address' />
              <TextInput
                type={TextInputType.Text}
                id='card_address'
                name='address'
                placeholder='1 Example St'
                ref={register({
                  required: 'Field is required',
                })}
                error={errors.address}
              />
              <ErrorMessage
                errors={errors}
                name='address'
                as={StyledFieldError}
              />
            </FormGroup>
          </LeftColumn>
          <RightColumn>
            <FormGroup>
              <Label text='City' htmlFor='card_city' />
              <TextInput
                type={TextInputType.Text}
                id='card_city'
                name='city'
                placeholder='Sydney'
                ref={register({
                  required: 'Field is required',
                })}
                error={errors.city}
              />
              <ErrorMessage errors={errors} name='city' as={StyledFieldError} />
            </FormGroup>
          </RightColumn>
        </FormRow>

        <FormRow>
          <LeftColumn>
            <FormGroup>
              <Label text='Country' />
              <Controller
                control={control}
                name='country'
                defaultValue={{ value: 'AU', label: 'Australia' }}
                as={Select}
                options={countries}
              />
              <ErrorMessage
                errors={errors}
                name='country'
                as={StyledFieldError}
              />
            </FormGroup>
          </LeftColumn>
          <RightColumn>
            <StateZip>
              <StateGroup>
                <Label text='State' htmlFor='card_state' />
                <TextInput
                  type={TextInputType.Text}
                  id='card_state'
                  name='state'
                  ref={register({
                    required: 'Field is required',
                  })}
                  error={errors.state}
                  placeholder='NSW'
                />
                <ErrorMessage
                  errors={errors}
                  name='state'
                  as={StyledFieldError}
                />
              </StateGroup>
              <ZipGroup>
                <Label text='Zip' htmlFor='card_zip' />
                <TextInput
                  type={TextInputType.Text}
                  id='card_zip'
                  name='zip'
                  ref={register({
                    required: 'Field is required',
                  })}
                  error={errors.zip}
                  placeholder='2600'
                />
                <ErrorMessage
                  errors={errors}
                  name='zip'
                  as={StyledFieldError}
                />
              </ZipGroup>
            </StateZip>
          </RightColumn>
        </FormRow>
      </form>
    </div>
  );
};

export default CreditCardForm;
